/**
 * The device status shown in the header of the asset page (tri-server #1173).
 *
 * One answer out of the asset's lifecycle fields and the server's latest operating state
 * (`GET /assets/:asset/operating-states/latest`), in a fixed order — the first rule that applies
 * wins. The precedence mirrors the app (tri-app `deviceStatus.ts`):
 *
 *   decommissioned > notActivated > asleep > notCommissioned > maintenance > critical
 *   > warning > normal > noData
 *
 * The server already folds maintenance mode and decommissioning into the endpoint; the
 * activation and commissioning stamps are not folded in, so they are read off the asset here.
 * Kept free of React and of the network so the precedence can be tested with plain objects.
 */

/** tri-model `OperatingState` values, spelled out to keep this module free of the model import. */
export const OPERATING_STATE = {
  normal: 0,
  warning: 1,
  critical: 2,
  maintenance: 3,
  decommissioned: 4,
  unknown: -1,
} as const;

export type AssetStatusKind =
  | "decommissioned"
  | "notActivated"
  | "asleep"
  | "notCommissioned"
  | "maintenance"
  | "critical"
  | "warning"
  | "normal"
  | "noData"
  /** Not resolved yet, or the state lookup failed — nothing can be said. */
  | "unknown";

export type AssetStatus = {
  kind: AssetStatusKind;
  /** When the state began, where the data says so (maintenance since, decommissioned on, …). */
  since: Date | null;
  /** The newest sign of life from the device, when known. */
  lastUplinkAt: Date | null;
  /** True when the device has not reported for more than {@link STALE_INTERVALS} send intervals. */
  stale: boolean;
};

/** A device counts as silent after this many missed send intervals. */
export const STALE_INTERVALS = 3;

/**
 * Seconds per unit of the configured `sendInterval` — the one place that decides what the number
 * means (tri-server `assetStatusSummary.ts`, tri-app `deviceStatus.ts`).
 *
 * The unit is MINUTES (tri-server #1434): FireIQ's device-type default is `sendInterval = 20160`,
 * which is 14 days — the real transmit cadence. Read as seconds (5.6 h) a healthy unit looked
 * "veraltet" after 17 hours. The unit is derived from that number; written confirmation from
 * Smart Sense / firmware is still pending. If they say otherwise, set this back to 1.
 */
export const SEND_INTERVAL_UNIT_SECONDS = 60;

/** FireIQ's default send interval in seconds (device-type catalogue `sendInterval` default, 20160 min = 14 days). */
export const DEFAULT_SEND_INTERVAL_SECONDS = 20160 * SEND_INTERVAL_UNIT_SECONDS;

/** Mind the spelling: the legal lifecycle state has two `s`, the model's enum member one. */
const DECOMMISSIONED_ASSET_STATE = "decommissioned";

/** The latest operating state as the endpoint returns it; `null` body = no measurement at all. */
export type LatestOperatingState = { value?: unknown; timestamp?: unknown } | null;

export const toDate = (value: unknown): Date | null => {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isSet = (value: unknown): boolean =>
  typeof value === "string" ? value.trim() !== "" : value != null && value !== false;

/** The send interval of the device in seconds (configured values are minutes): the asset's own configuration, then the device type's default. */
export function sendIntervalSeconds(asset: any, deviceType?: any): number {
  const own = Number(asset?.iotConfiguration?.sendInterval);
  if (Number.isFinite(own) && own > 0) return own * SEND_INTERVAL_UNIT_SECONDS;
  const parameters: any[] = Array.isArray(deviceType?.configuration) ? deviceType.configuration : [];
  const declared = Number(parameters.find((parameter) => parameter?.id === "sendInterval")?.defaultValue);
  if (Number.isFinite(declared) && declared > 0) return declared * SEND_INTERVAL_UNIT_SECONDS;
  return DEFAULT_SEND_INTERVAL_SECONDS;
}

/** The newest of the given instants, or null when none is a valid date. */
export function newestDate(...values: unknown[]): Date | null {
  return values
    .map(toDate)
    .reduce<Date | null>((newest, date) => (date && (!newest || date > newest) ? date : newest), null);
}

/**
 * Whether the asset is in service: the commissioning stamp, a place on a floor plan, and — for
 * assets never activated as a device (registered before the stamp existed) — any operating state.
 */
export function isInService(asset: any, hasOperatingState: boolean): boolean {
  if (!asset) return false;
  if (isSet(asset.commissioned)) return true;
  if (isSet(asset.floor) || isSet(asset.space)) return true;
  return !isSet(asset.activated) && hasOperatingState;
}

/** Put to sleep for shipping: the sleep stamp is newer than the last activation (or there is none). */
export function isAsleep(asset: any): boolean {
  const slept = toDate(asset?.sleptAt);
  if (!slept) return false;
  const activated = toDate(asset?.activated);
  return !activated || slept.getTime() >= activated.getTime();
}

export type ResolveAssetStatusInput = {
  asset: any;
  /** `undefined` = not resolved yet, `null` = the endpoint knows no operating state at all. */
  operatingState: LatestOperatingState | undefined;
  /** Send interval in seconds; defaults to the asset's own, see {@link sendIntervalSeconds}. */
  sendIntervalSeconds?: number;
  now?: number;
};

export function resolveAssetStatus({
  asset,
  operatingState,
  sendIntervalSeconds: givenInterval,
  now = Date.now(),
}: ResolveAssetStatusInput): AssetStatus {
  const interval = givenInterval ?? sendIntervalSeconds(asset);
  const raw = operatingState?.value;
  const value = raw == null || raw === "" || Number.isNaN(Number(raw)) ? undefined : Number(raw);
  const stateAt = toDate(operatingState?.timestamp);
  // A reading-derived state is written at the reading's instant, so its timestamp is a sign of
  // life too; maintenance/decommission points are not.
  const readingDerived =
    value === OPERATING_STATE.normal || value === OPERATING_STATE.warning || value === OPERATING_STATE.critical;
  const uplink = readingDerived ? stateAt : null;
  const isIot = isSet(asset?.iotDeviceType);
  const stale = isIot && uplink != null && now - uplink.getTime() > STALE_INTERVALS * interval * 1000;
  const result = (kind: AssetStatusKind, since: Date | null = null): AssetStatus => ({
    kind,
    since,
    lastUplinkAt: uplink,
    stale,
  });

  if (asset?.assetState === DECOMMISSIONED_ASSET_STATE || value === OPERATING_STATE.decommissioned) {
    return result("decommissioned", toDate(asset?.decommissionedAt) ?? stateAt);
  }
  if (operatingState === undefined || (operatingState !== null && value === OPERATING_STATE.unknown)) {
    return result("unknown");
  }

  const hasOperatingState = operatingState !== null;
  if (isIot) {
    // The activation stamp is missing on some activated units, so a unit that has ever reported
    // counts as activated; so does a sleep stamp (only an activated unit can be put to sleep).
    if (!isSet(asset?.activated) && !hasOperatingState && !isAsleep(asset) && !isInService(asset, false)) {
      return result("notActivated");
    }
    if (isAsleep(asset)) return result("asleep", toDate(asset.sleptAt));
    if (!isInService(asset, hasOperatingState)) return result("notCommissioned", toDate(asset?.activated));
  }

  switch (value) {
    case OPERATING_STATE.maintenance:
      return result("maintenance", toDate(asset?.maintenanceSince) ?? stateAt);
    case OPERATING_STATE.critical:
      return result("critical", stateAt);
    case OPERATING_STATE.warning:
      return result("warning", stateAt);
    case OPERATING_STATE.normal:
      // A "normal" from a device that went silent is a false green — critical/warning stay.
      return stale ? result("noData") : result("normal", stateAt);
    default:
      return result("noData");
  }
}

/** Badge variants of the design system's `Badge`. */
export type AssetStatusVariant = "default" | "secondary" | "destructive" | "success" | "warning" | "outline";

export const STATUS_VARIANT: Record<AssetStatusKind, AssetStatusVariant> = {
  decommissioned: "secondary",
  notActivated: "outline",
  asleep: "outline",
  notCommissioned: "outline",
  maintenance: "default",
  critical: "destructive",
  warning: "warning",
  normal: "success",
  noData: "secondary",
  unknown: "outline",
};

/** The i18n key suffix of the reason line under the badge (`assetStatus.reason.<key>`). */
export function statusReasonKey(status: AssetStatus): string {
  if (status.kind === "noData") return status.stale ? "noDataSilent" : "noData";
  return status.kind;
}

/** Date formatting for the lifecycle fields; missing or invalid values read "-". */
export function formatLifecycleDate(value: unknown, withTime = false): string {
  const date = toDate(value);
  if (!date) return "-";
  return withTime ? `${date.toLocaleDateString()} ${date.toLocaleTimeString()}` : date.toLocaleDateString();
}

export type LifecycleRow = { key: string; value: string };

/**
 * The lifecycle fields of the General tab, in reading order. Sleep and maintenance stamps only
 * appear while they apply; the rest always shows, with "-" where the asset has no value.
 */
export function lifecycleRows(asset: any): LifecycleRow[] {
  const rows: LifecycleRow[] = [
    { key: "installed", value: formatLifecycleDate(asset?.installed) },
    { key: "activated", value: formatLifecycleDate(asset?.activated, true) },
    { key: "commissioned", value: formatLifecycleDate(asset?.commissioned, true) },
  ];
  if (isAsleep(asset)) rows.push({ key: "sleptAt", value: formatLifecycleDate(asset.sleptAt, true) });
  if (isSet(asset?.maintenanceSince)) {
    rows.push({ key: "maintenanceSince", value: formatLifecycleDate(asset.maintenanceSince, true) });
  }
  if (asset?.assetState === DECOMMISSIONED_ASSET_STATE) {
    rows.push({ key: "decommissionedAt", value: formatLifecycleDate(asset.decommissionedAt, true) });
  }
  rows.push(
    { key: "lastMaintained", value: formatLifecycleDate(asset?.lastMaintained) },
    { key: "nextMaintenance", value: formatLifecycleDate(asset?.nextMaintenance) },
    { key: "lastChecked", value: formatLifecycleDate(asset?.lastChecked) },
    { key: "nextCheck", value: formatLifecycleDate(asset?.nextCheck) },
  );
  return rows;
}

/** True when a date-only deadline lies before today (the due day itself is not yet overdue). */
export function isOverdue(value: unknown, now: number = Date.now()): boolean {
  const date = toDate(value);
  if (!date) return false;
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  return date.getTime() < startOfToday.getTime();
}
